import { useEffect, useMemo, useRef, useState } from "react"

import { Badge } from "@coval/ui/badge"
import { Button } from "@coval/ui/button"
import { Card, CardContent } from "@coval/ui/card"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@coval/ui/dialog"
import { Progress } from "@coval/ui/progress"
import { ChevronDown, ChevronUp } from "lucide-react"

import type { Annotation } from "@/types/api/Annotation"
import type { MetricOutputsByMetric } from "@/types/api/MetricOutputsByMetric"
import type { MetricRecordData } from "@/types/metrics/contracts"

import type { MetricManager } from "../optimization/constants"
import type {
  ImprovementConfig,
  ImprovementFocus,
} from "../optimization/optimization"
import { calculateDisagreement } from "./feedbackAnalysis"
import { FeedbackPreviewPanel } from "./FeedbackPreviewPanel"
import { ImprovementFocusSelector } from "./ImprovementFocusSelector"

interface ContextPreviewModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (config: ImprovementConfig) => void
  currentPrompt: string
  metricType?: MetricManager
  annotations: Annotation[]
  metricOutputs: MetricOutputsByMetric[string]
  metric: MetricRecordData
  isLoading: boolean
  errorMessage?: string | null
  currentUserId?: string
  totalAnnotationCount?: number
  feedbackUnavailableReason?: string
}

const METRIC_TYPE_LABELS: Record<string, string> = {
  manager_llm_binary: "Binary",
  manager_toolcall: "Tool Call",
  manager_default_categorical: "Categorical",
  manager_numerical_llm_judge: "Numerical",
  manager_audio_llm_judge: "Audio Binary",
  manager_audio_llm_binary: "Audio Binary",
  manager_audio_llm_categorical: "Audio Categorical",
  manager_audio_llm_numerical: "Audio Numerical",
  manager_words_per_message_with_threshold: "Words Per Message (Threshold)",
}

export const ContextPreviewModal = ({
  isOpen,
  onClose,
  onConfirm,
  currentPrompt,
  metricType,
  annotations,
  metricOutputs,
  metric,
  isLoading,
  errorMessage,
  currentUserId,
  totalAnnotationCount,
  feedbackUnavailableReason,
}: ContextPreviewModalProps) => {
  const [selectedAnnotationIds, setSelectedAnnotationIds] = useState<string[]>(
    [],
  )
  const [improvementFocus, setImprovementFocus] =
    useState<ImprovementFocus>("general")
  const [isPromptExpanded, setIsPromptExpanded] = useState(false)
  const [progress, setProgress] = useState(0)
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const disagreementData = useMemo(
    () => calculateDisagreement({ annotations, metricOutputs, metric }),
    [annotations, metric, metricOutputs],
  )

  const hasHighMachineDisagreement =
    disagreementData.machineDisagreementRate > 0.3
  const hasFeedback = annotations.length > 0 && !feedbackUnavailableReason

  useEffect(() => {
    if (isOpen) {
      if (hasFeedback) {
        setSelectedAnnotationIds(annotations.map((a) => a.id))
        setImprovementFocus("feedback")
      } else {
        setSelectedAnnotationIds([])
        setImprovementFocus("general")
      }
    }
  }, [isOpen, annotations, hasFeedback])

  useEffect(() => {
    if (improvementFocus === "feedback" && selectedAnnotationIds.length === 0) {
      setImprovementFocus("general")
    }
  }, [improvementFocus, selectedAnnotationIds.length])

  const progressRef = useRef(0)

  useEffect(() => {
    if (isLoading) {
      setProgress(0)
      progressRef.current = 0
      progressIntervalRef.current = setInterval(() => {
        setProgress((prev) => {
          const next = prev >= 90 ? prev : prev + 2
          progressRef.current = next
          return next
        })
      }, 100)
    } else {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
        progressIntervalRef.current = null
      }
      if (progressRef.current > 0) {
        setProgress(100)
        setTimeout(() => setProgress(0), 300)
      }
      progressRef.current = 0
    }

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
      }
    }
  }, [isLoading])

  const handleConfirm = () => {
    onConfirm({
      selectedAnnotationIds,
      improvementFocus,
      includeDisagreementAnalysis: hasHighMachineDisagreement,
    })
  }

  const truncatedPrompt = useMemo(() => {
    if (currentPrompt.length <= 200) {
      return currentPrompt
    }
    return `${currentPrompt.substring(0, 200)}...`
  }, [currentPrompt])

  const metricTypeLabel = metricType
    ? METRIC_TYPE_LABELS[metricType] || metricType
    : "Unknown"

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-full max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Get Prompt Suggestions</span>
            <Badge size="status" className="ml-2">
              {metricTypeLabel}
            </Badge>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Choose whether prompt suggestions should use reviewer feedback and
            paired AI labels.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="gap-4 overflow-y-auto">
          <Card className="border-none shadow-none">
            <CardContent className="gap-2">
              <div className="flex justify-between items-center">
                <h4 className="font-semibold text-sm">Current Prompt</h4>
                <Button
                  variant="ghost"
                  aria-label={
                    isPromptExpanded ? "Collapse prompt" : "Expand prompt"
                  }
                  onClick={() => setIsPromptExpanded(!isPromptExpanded)}
                >
                  {isPromptExpanded ? "Collapse" : "Expand"}
                  {isPromptExpanded ? (
                    <ChevronUp className="w-4 h-4" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="w-4 h-4" aria-hidden="true" />
                  )}
                </Button>
              </div>
              <div
                className={`bg-surface-1 rounded-lg p-3 ${
                  isPromptExpanded ? "" : "max-h-24 overflow-hidden"
                }`}
              >
                <pre className="text-xs whitespace-pre-wrap font-mono text-coval-text-muted">
                  {isPromptExpanded ? currentPrompt : truncatedPrompt}
                </pre>
              </div>
              <p className="text-xs text-coval-text-muted">
                {currentPrompt.length} characters
              </p>
            </CardContent>
          </Card>

          {feedbackUnavailableReason ? (
            <Card className="border-none shadow-none">
              <CardContent className="gap-2" role="alert">
                <h4 className="font-semibold text-sm">Feedback unavailable</h4>
                <p className="text-sm text-coval-text-muted">
                  {feedbackUnavailableReason}
                </p>
              </CardContent>
            </Card>
          ) : hasFeedback ? (
            <FeedbackPreviewPanel
              annotations={annotations}
              selectedIds={selectedAnnotationIds}
              onSelectionChange={setSelectedAnnotationIds}
              currentUserId={currentUserId}
              disagreementData={disagreementData}
              totalAnnotationCount={totalAnnotationCount}
              isActive={improvementFocus === "feedback"}
            />
          ) : (
            <Card className="border-none shadow-none">
              <CardContent className="gap-2">
                <h4 className="font-semibold text-sm">
                  Annotations to Include
                </h4>
                <p className="text-sm text-coval-text-muted">
                  No annotations found for this metric. Suggestions will be
                  based on prompt engineering best practices for{" "}
                  {metricTypeLabel.toLowerCase()} metrics.
                </p>
              </CardContent>
            </Card>
          )}

          <ImprovementFocusSelector
            value={improvementFocus}
            onChange={setImprovementFocus}
            hasFeedback={hasFeedback && selectedAnnotationIds.length > 0}
            hasHighMachineDisagreement={hasHighMachineDisagreement}
          />
        </DialogBody>

        <DialogFooter className="flex-col gap-3">
          {errorMessage && !isLoading && (
            <div
              role="alert"
              className="w-full rounded-md border border-coval-danger-border bg-coval-danger-surface px-3 py-2 text-sm text-coval-danger-on-surface"
            >
              {errorMessage}
            </div>
          )}
          {isLoading && (
            <div className="w-full">
              <Progress
                value={progress}
                className="w-full"
                aria-label="Generating suggestions"
              />
              <p className="text-xs text-coval-text-muted mt-1 text-center">
                Analyzing prompt and generating suggestions...
              </p>
            </div>
          )}
          <div className="flex justify-between w-full">
            <Button
              variant="secondary"
              aria-label="Cancel"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              aria-label="Get suggestions"
              onClick={handleConfirm}
              disabled={
                isLoading ||
                !currentPrompt ||
                currentPrompt.trim() === "" ||
                (improvementFocus === "feedback" &&
                  selectedAnnotationIds.length === 0)
              }
            >
              {isLoading ? "Generating..." : "Get Suggestions"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
